import '../css/app.css'
import './bootstrap'
import { createInertiaApp, router } from '@inertiajs/vue3'
import { createApp, h } from 'vue'
import { identifyVisitor } from './composables/useAnalytics'
import { reveal } from './directives/reveal'

/**
 * Replay the page entrance on every Inertia navigation.
 *
 * Inertia swaps the component without a document load, so a CSS animation
 * declared on <main> only ever runs once — on the first page. Removing and
 * re-adding the class, with a forced reflow between, restarts it.
 *
 * It is deliberately on <main> and not on <body>: the header and the drawer
 * should stay put while the content changes.
 */
const replayPageEnter = () => {
  const main = document.querySelector('main')
  if (!main) return

  main.classList.remove('page-enter')
  void main.offsetWidth
  main.classList.add('page-enter')
}

/**
 * A new page starts at the top, or at the thing that was asked for.
 *
 * Inertia swaps the component without a document load, so the browser's own
 * two behaviours never run: it does not return to the top, and it does not jump
 * to a `#fragment`. The first left a visitor who clicked a link half way down a
 * long page — the FAQ index, a footer link — arriving at the same offset on a
 * page whose content is somewhere else entirely. The second made every
 * in-page anchor silently do nothing.
 *
 * `preserveScroll` on a visit is respected: it sets `preserveScroll` on the
 * event, and those are deliberate — a filter re-query that should not throw the
 * reader back to the top.
 */
const scrollToTarget = () => {
  const { hash } = window.location

  if (hash && hash.length > 1) {
    // A fragment can name an element that this page renders after mounting, so
    // the lookup happens on the frame the new component is painted, not before.
    const target = document.getElementById(decodeURIComponent(hash.slice(1)))

    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })

      return
    }
  }

  window.scrollTo({ top: 0, left: 0 })
}

// Sync CSRF token meta tag on each Inertia navigation
router.on('navigate', (event) => {
  const csrfToken = event.detail.page.props.csrf_token
  if (csrfToken) {
    const meta = document.querySelector('meta[name="csrf-token"]')
    if (meta) {
      meta.setAttribute('content', csrfToken)
    }
  }

  identifyVisitor(event.detail.page.props.auth?.user)
  requestAnimationFrame(() => {
    replayPageEnter()

    if (!event.detail.visit?.preserveScroll) {
      scrollToTarget()
    }
  })
})

createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob('./Pages/**/*.vue', { eager: true })
    return pages[`./Pages/${name}.vue`]
  },
  setup({ el, App, props, plugin }) {
    createApp({ render: () => h(App, props) })
      .use(plugin)
      .directive('reveal', reveal)
      .mount(el)

    identifyVisitor(props.initialPage.props.auth?.user)
    requestAnimationFrame(() => {
      replayPageEnter()

      /*
       * Only when a fragment was asked for. On a first load the browser has
       * already restored the reader's own scroll position — on a refresh, or on
       * the back button — and moving it would throw that away. But the element
       * a fragment names does not exist until Vue has mounted, so the browser's
       * attempt at it happened against an empty page and failed silently.
       */
      if (window.location.hash.length > 1) {
        scrollToTarget()
      }
    })
  },
})
